import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TIER_STYLE = {
  cold:        { bg: '#eef2ff', color: '#4f46e5' },
  warm:        { bg: '#fffbeb', color: '#d97706' },
  hot:         { bg: '#fef2f2', color: '#dc2626' },
  sales_ready: { bg: '#ecfdf5', color: '#059669' },
  disqualified:{ bg: '#f3f4f6', color: '#9ca3af' },
  archived:    { bg: '#f3f4f6', color: '#9ca3af' },
};

function TierBadge({ tier }) {
  const s = TIER_STYLE[tier] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
      {tier?.replace('_', ' ')}
    </span>
  );
}

function ScoreBar({ score }) {
  const color = score >= 86 ? '#10b981' : score >= 61 ? '#ef4444' : score >= 31 ? '#f59e0b' : '#6366f1';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, background: 'var(--border)', borderRadius: 4, height: 6 }}>
        <div style={{ width: `${score}%`, background: color, height: '100%', borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 24, textAlign: 'right' }}>{score}</span>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ tier: '', source: '', search: '', page: 1 });
  const [showImport, setShowImport] = useState(false);
  const navigate = useNavigate();

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '')
      );
      const { data } = await axios.get('/api/leads', { params });
      setLeads(data.leads);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post('/api/leads/csv', form);
      alert(`Imported ${data.created} leads (${data.skipped} skipped)`);
      fetchLeads();
    } catch (err) {
      alert('Import failed: ' + (err.response?.data?.error || err.message));
    }
    setShowImport(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Leads</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 2 }}>{total} total leads</p>
        </div>
        <button onClick={() => setShowImport(true)} style={{
          background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', fontWeight: 600,
          borderRadius: 7, padding: '9px 18px', cursor: 'pointer', fontSize: 14,
        }}>Import CSV</button>
      </div>

      {showImport && (
        <div style={{
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 24, marginBottom: 20,
        }}>
          <h3 style={{ marginBottom: 12, fontSize: 15 }}>Import leads from CSV</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
            Columns supported: first_name, last_name, email, phone, company, industry, city, province, service_interest
          </p>
          <input type="file" accept=".csv" onChange={handleCsvUpload} />
          <button onClick={() => setShowImport(false)} style={{
            marginLeft: 12, background: 'none', border: '1px solid var(--border)',
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--vela-text)',
          }}>Cancel</button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Search name, email, phone..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 14, width: 240, background: 'var(--card-bg)', color: 'var(--vela-text)' }}
        />
        <select value={filters.tier} onChange={e => setFilters(f => ({ ...f, tier: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 14, background: 'var(--card-bg)', color: 'var(--vela-text)' }}>
          <option value="">All tiers</option>
          <option value="cold">Cold</option>
          <option value="warm">Warm</option>
          <option value="hot">Hot</option>
          <option value="sales_ready">Sales Ready</option>
        </select>
        <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value, page: 1 }))}
          style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 14, background: 'var(--card-bg)', color: 'var(--vela-text)' }}>
          <option value="">All sources</option>
          <option value="google">Google</option>
          <option value="facebook">Facebook</option>
          <option value="organic">Organic</option>
          <option value="referral">Referral</option>
          <option value="csv_import">CSV Import</option>
        </select>
      </div>

      {/* Lead table */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--border)' }}>
              {['Name', 'Email', 'Phone', 'Industry', 'Source', 'Tier', 'Score', 'Added'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</td></tr>
            )}
            {!loading && !leads.length && (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                No leads yet. Import a CSV or share your capture form link to get started.
              </td></tr>
            )}
            {leads.map(lead => (
              <tr key={lead.id}
                onClick={() => navigate(`/leads/${lead.id}`)}
                style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                  {`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—'}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{lead.email}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{lead.phone || '—'}</td>
                <td style={{ padding: '12px 16px', textTransform: 'capitalize' }}>{lead.industry || '—'}</td>
                <td style={{ padding: '12px 16px', textTransform: 'capitalize' }}>{lead.source?.replace('_', ' ') || '—'}</td>
                <td style={{ padding: '12px 16px' }}><TierBadge tier={lead.tier} /></td>
                <td style={{ padding: '12px 16px', minWidth: 100 }}><ScoreBar score={lead.score} /></td>
                <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>
                  {new Date(lead.created_at).toLocaleDateString('en-CA')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button disabled={filters.page <= 1}
            onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', background: 'var(--card-bg)' }}>
            ← Prev
          </button>
          <span style={{ padding: '6px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
            Page {filters.page} of {Math.ceil(total / 50)}
          </span>
          <button disabled={filters.page * 50 >= total}
            onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', background: 'var(--card-bg)' }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
